import { BusinessObjectBase } from './base';
import { accountUserSchema } from '@schemas/business-objects/account-user';
import type { Account } from './account';
import type { User } from './user';
import type { Id } from '../base-types';

export interface AccountUserData {
  readonly id: number;
  readonly isAdmin?: boolean;
  readonly isDesigner?: boolean;
  readonly accountId: number;
  readonly account?: Account;
  readonly userId: Id;
  readonly user?: User;
}

export interface AccountUser extends Readonly<AccountUserData> {}
type AccountUserCreateWithChildrenData = Partial<AccountUserData>;

export class AccountUser extends BusinessObjectBase {
  static readonly entityName = 'AccountUser' as const;
  static readonly plural = 'account-users';
  static schema = accountUserSchema;

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<AccountUser[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new AccountUser(data));
  }

  static async findOne(filter?: any): Promise<AccountUser | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new AccountUser(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<AccountUser> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new AccountUser(data);
  }

  static async create(data: Omit<AccountUserData, 'id'>): Promise<AccountUser> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new AccountUser(result);
  }

  static async createMany(
    data: Omit<AccountUserData, 'id'>[],
  ): Promise<AccountUser[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new AccountUser(item));
  }

  static async createWithChildren(
    data: AccountUserCreateWithChildrenData,
  ): Promise<AccountUser> {
    const url = `${this.baseUrl}/${this.plural}/create-with-children`;
    const result = await this.post<any>(url, data);
    return new AccountUser(result);
  }

  static async createManyWithChildren(
    data: AccountUserCreateWithChildrenData[],
  ): Promise<AccountUser[]> {
    const url = `${this.baseUrl}/${this.plural}/create-many-with-children`;
    const results = await this.post<any[]>(url, data);
    return results.map((item: any) => new AccountUser(item));
  }

  static async updateById(
    id: number,
    updates: Partial<AccountUserData>,
  ): Promise<AccountUser> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    const result = await this.patch<any>(url, updates);
    return new AccountUser(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${encodeURIComponent(String(id))}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
